/**
 * JSON-LD Schema Utilities for SEO
 * Generates structured data for search engines
 */

export interface StructuredData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

/**
 * Organization Schema - For homepage and general pages
 */
export const getOrganizationSchema = (): StructuredData => ({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  'name': 'Virewire',
  'url': 'https://virewire.com',
  'logo': 'https://virewire.com/logo.png',
  'description': 'Professional property services including cleaning, maintenance, and logistics across the UK',
  'telephone': '+44 800 123 4567',
  'email': 'info@virewire.com',
  'address': {
    '@type': 'PostalAddress',
    'streetAddress': '123 Business Park',
    'addressLocality': 'London',
    'addressRegion': 'London',
    'postalCode': 'EC1A 1BB',
    'addressCountry': 'GB'
  },
  'sameAs': [
    'https://www.facebook.com/virewire',
    'https://www.twitter.com/virewire',
    'https://www.linkedin.com/company/virewire',
    'https://www.instagram.com/virewire'
  ],
  'contactPoint': {
    '@type': 'ContactPoint',
    'telephone': '+44 800 123 4567',
    'contactType': 'Customer Service'
  }
});

/**
 * LocalBusiness Schema - For location/sector pages
 */
export const getLocalBusinessSchema = (locationName: string, latitude: number, longitude: number): StructuredData => ({
  '@context': 'https://schema.org/',
  '@type': 'LocalBusiness',
  'name': `Virewire - ${locationName}`,
  'url': `https://virewire.com/${locationName.toLowerCase()}`,
  'description': `Professional property services in ${locationName}`,
  'telephone': '+44 800 123 4567',
  'email': 'info@virewire.com',
  'address': {
    '@type': 'PostalAddress',
    'addressLocality': locationName,
    'addressRegion': locationName,
    'addressCountry': 'GB'
  },
  'geo': {
    '@type': 'GeoCoordinates',
    'latitude': latitude,
    'longitude': longitude
  },
  'areaServed': [locationName, 'UK'],
  'image': 'https://virewire.com/logo.png'
});

/**
 * Service Schema - For service detail pages
 */
export const getServiceSchema = (options: {
  name: string;
  description: string;
  image?: string;
  price?: string;
  priceCurrency?: string;
  areaServed?: string[];
  provider?: string;
}): StructuredData => {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Service',
    'name': options.name,
    'description': options.description,
    'image': options.image || 'https://virewire.com/logo.png',
    'provider': {
      '@type': 'Organization',
      'name': options.provider || 'Virewire',
      'url': 'https://virewire.com'
    },
    'areaServed': options.areaServed || ['London', 'Manchester', 'Birmingham', 'UK']
  };

  if (options.price) {
    schema.offers = {
      '@type': 'Offer',
      'price': options.price,
      'priceCurrency': options.priceCurrency || 'GBP',
      'availability': 'https://schema.org/InStock'
    };
  }

  return schema;
};

/**
 * FAQPage Schema - For FAQ sections
 */
export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>): StructuredData => ({
  '@context': 'https://schema.org/',
  '@type': 'FAQPage',
  'mainEntity': faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer
    }
  }))
});

/**
 * AggregateRating Schema - For review sections
 */
export const getAggregateRatingSchema = (options: {
  ratingValue: number;
  ratingCount: number;
  reviewCount: number;
}): StructuredData => ({
  '@context': 'https://schema.org/',
  '@type': 'AggregateRating',
  'ratingValue': options.ratingValue.toFixed(2),
  'ratingCount': options.ratingCount,
  'reviewCount': options.reviewCount
});

/**
 * Product/Service with Reviews Schema
 */
export const getProductWithReviewsSchema = (options: {
  name: string;
  description: string;
  image?: string;
  price?: string;
  priceCurrency?: string;
  ratingValue?: number;
  ratingCount?: number;
  reviewCount?: number;
}): StructuredData => {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    'name': options.name,
    'description': options.description,
    'image': options.image || 'https://virewire.com/logo.png'
  };

  if (options.price) {
    schema.offers = {
      '@type': 'Offer',
      'price': options.price,
      'priceCurrency': options.priceCurrency || 'GBP'
    };
  }

  if (options.ratingValue !== undefined) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': options.ratingValue.toFixed(2),
      'ratingCount': options.ratingCount || 0,
      'reviewCount': options.reviewCount || 0
    };
  }

  return schema;
};

/**
 * Breadcrumb Schema
 */
export const getBreadcrumbSchema = (items: Array<{ name: string; path?: string; url?: string }>): StructuredData => {
  const mappedItems = items.map((item, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': item.name,
    'item': item.url || `https://virewire.com${item.path || '/'}`
  }));

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    'itemListElement': mappedItems
  };
};

/**
 * Collection Page Schema - For category/collection pages
 */
export const getCollectionPageSchema = (options: {
  name: string;
  description: string;
  url: string;
  itemCount?: number;
  image?: string;
}): StructuredData => ({
  '@context': 'https://schema.org/',
  '@type': 'CollectionPage',
  'name': options.name,
  'description': options.description,
  'url': options.url,
  'image': options.image || 'https://virewire.com/logo.png',
  'numberOfItems': options.itemCount || 0
});

/**
 * WebPage Schema - For generic pages
 */
export const getWebPageSchema = (options: {
  name: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  image?: string;
}): StructuredData => {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    'name': options.name,
    'description': options.description,
    'url': options.url,
    'image': options.image || 'https://virewire.com/logo.png',
    'datePublished': options.datePublished || new Date().toISOString(),
    'dateModified': options.dateModified || new Date().toISOString()
  };

  if (options.author) {
    schema.author = {
      '@type': 'Organization',
      'name': options.author
    };
  }

  return schema;
};

/**
 * Helper to create a JSON-LD script tag string
 */
export const createJsonLdScript = (schema: StructuredData): string => {
  return JSON.stringify(schema);
};
